import React, { useState, useEffect, useCallback, useMemo } from "react";
import CardTable from "components/CardTable/CardTable";
import { TableInputs } from "components/CardTable/TableInputs";
import { useOpenComponent } from "hooks/useOpenComponent";

import { useDispatch, useSelector } from "react-redux";

import AddIcon from "@mui/icons-material/Add";
import { columns } from "./columns";

import RegularButton from "components/CustomButtons/Button";
import { Home } from "@mui/icons-material";
import { useSearch } from "hooks/useSearch";
import { ModalV2 } from "components/Modal/ModalV2";

import { ROUTES_CYCLES_PHRASE } from "./config";
import { RouteCyclesForm } from "./RouteCyclesForm";
import {
  fetchRouteCycles,
  clearRouteCycles,
  newRouteCycle,
  sendOpenRouteCycle,
  handleRouteCyclesInputsAction,
} from "redux/actions/buildings/routeCycles/routeCyclesActions";
import { useToggle } from "hooks/useToggle";
import { Modal } from "components/Modal/Modal";

const LOADINGS = [
  "loadingRouteCycles",
  "loadingNewRouteCycle",
  "loadingOpenRouteCycle"
];

export const RoutesCyclesPage = () => {
  const dispatch = useDispatch();
  const { isOpen, handleIsOpen } = useOpenComponent();

  const {
    setSnackbar,
    clearSearch,
    buildCompLoadings
  } = useSearch();
  const { isLoadingComp } = useSelector(
    state => state.search
  );
  const [idRoute, setIdRoute] = useState("");
  const [openModal, setOpenModal] = useToggle();
  const {
    routeCycles,
    routesMap,
    collectPeriodsMap,
    employees,
    station,
    isAddMode,
    deleteMode,
  } = useSelector(
    state => state.routeCycles
  );

  const handleOpenModal = route => {
    setOpenModal();
    setIdRoute(route);
  };

  const getRouteCycles = useCallback(
    stationID => {
      dispatch(fetchRouteCycles(stationID));
    },
    [dispatch]
  );

  const routeCyclesMemo = useMemo(
    () =>
      routeCycles.map(routeCycle => {
        const newRoute = routesMap ? routesMap[routeCycle.idRoute] : {};
        const newCollectPeriod = collectPeriodsMap
          ? collectPeriodsMap[routeCycle.idCollectPeriod]
          : {};

        const newEmployee = employees
          ? employees.find(employee => employee.id === routeCycle.idEmployee)
          : {};

        return {
          ...routeCycle,
          route: newRoute?.name ?? "",
          collectPeriod: newCollectPeriod?.name ?? "",
          employee: newEmployee?.name ?? "",
          modal: handleOpenModal
        };
      }),
    [routeCycles, routesMap, collectPeriodsMap]
  );

  const handleSubmit = useCallback(() => {
    if (station.length < 1) {
      setSnackbar(ROUTES_CYCLES_PHRASE.search.messages.noStations);
      return;
    }
    const stationID = station.id;
    getRouteCycles(stationID);
  }, [station, setSnackbar]);

  const submitMemoized = useCallback((e) => {
    e.preventDefault();
    handleSubmit();
  }, [handleSubmit]);

  const handleClose = () => {
    handleIsOpen();
  };

  const handleSendRoute = useCallback(() => {
    dispatch(sendOpenRouteCycle(idRoute, () => setOpenModal()));
  }, [idRoute]);

  const handleOpenAdd = () => {
    dispatch(newRouteCycle());
    handleIsOpen();
  };

  useEffect(() => {
    clearSearch();
    dispatch(clearRouteCycles());
  }, [clearSearch]);

  useEffect(() => {
    buildCompLoadings(LOADINGS);
  }, []);

  return (
    <React.Fragment>
      <CardTable
        data={routeCyclesMemo}
        columns={columns}
        loading={isLoadingComp?.loadingRouteCycles}
        title={ROUTES_CYCLES_PHRASE.page.table.title}
        icon={<Home />}
        hasHeader
        blockBtn
        hasControl
        controls={
          true && (
            <RegularButton
              size="sm"
              round
              justIcon
              color="info"
              style={{ margin: 10 }}
              onClick={handleOpenAdd}
            >
              <AddIcon style={{ margin: 0 }} />
            </RegularButton>
          )
        }
        tableContent={
          <TableInputs
            reducer="routeCycles"
            stations
            dispatchAction={handleRouteCyclesInputsAction}
            submitAction={submitMemoized}
          />
        }
      />
      <ModalV2
        open={isOpen}
        onClose={handleClose}
        toRight
        title={isAddMode
          ? ROUTES_CYCLES_PHRASE.modal.titles.add
          : deleteMode
            ? ROUTES_CYCLES_PHRASE.modal.titles.delete
            : ROUTES_CYCLES_PHRASE.modal.titles.edit}
      >
        {<RouteCyclesForm handleClose={handleClose} />}
      </ModalV2>
      <Modal
        isOpen={openModal}
        handleClose={setOpenModal}
        textButton1={ROUTES_CYCLES_PHRASE.modal.actions.submit}
        textButton2={ROUTES_CYCLES_PHRASE.modal.actions.cancel}
        actionFunction1={setOpenModal}
        actionFunction2={handleSendRoute}
        textContent={ROUTES_CYCLES_PHRASE.modal.content.open}
        isDisabled={isLoadingComp.loadingOpenRouteCycle}
      />
    </React.Fragment>
  );
};
